import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  IconButton,
  Box,
  Typography,
  Modal,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import Chat from "./Chat";
import Media from "./Media";
import { useSelector } from "react-redux";

const Communicator = ({ selectedApplicationId }) => {
  const selectedApplicationName = useSelector(
    (state) => state.intakeData.selectedAppIdName
  );
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [error, setError] = useState(null);
  const [senderEmail, setSenderEmail] = useState("");
  const popupRef = useRef(null);
  const [activeView, setActiveView] = useState("chat");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [processedMessages, setProcessedMessages] = useState([]);
  const [modalHandlers, setModalHandlers] = useState({
    onConfirm: null,
    onCancel: null,
  });
  // Modal and email formatting state
  const [showModal, setShowModal] = useState(false);
  const [rewrittenEmail, setRewrittenEmail] = useState(null);
  const [pendingReplyData, setPendingReplyData] = useState(null);
  const [loadingRewrite, setLoadingRewrite] = useState(false);

  const togglePopup = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (conversations?.attachments) {
      setMediaFiles(conversations.attachments);
    } else {
      setMediaFiles([]);
    }
  }, [conversations]);

  useEffect(() => {
    setSenderEmail(conversations?.history?.users[0]?.email_id);
  }, [conversations]);

  const handleSendReply = (
    replyMessage,
    senderEmail,
    receiverEmail,
    attachmentFile
  ) => {
    const newReply = {
      date_time: new Date().toISOString(),
      subject: "Re: Your Inquiry",
      sender_email: senderEmail,
      recipient_emails: [receiverEmail],
      body: replyMessage,
      attachments: [attachmentFile],
    };

    const updatedConversations = {
      ...conversations,
      history: {
        ...conversations.history,
        messages: [...conversations.history.messages, newReply],
      },
    };
    setConversations(updatedConversations);
  };

  const handleRewriteAndConfirm = async (
    replyMessage,
    senderEmail,
    receiverEmail,
    attachmentFile
  ) => {
    if (!replyMessage.trim()) return null;

    setLoadingRewrite(true);
    setPendingReplyData({ senderEmail, receiverEmail });

    try {
      const form = new URLSearchParams();
      form.append("history", JSON.stringify(conversations.history || {}));
      form.append("email_body", replyMessage);
      form.append("from_email", senderEmail);
      form.append("to_emails", receiverEmail);

      const res = await axios.post(
        "https://insureai-communicator-1074099157700.us-central1.run.app/api/v1/rewritter/rewrite_email_by_history",
        form,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
          },
        }
      );

      const rewritten = res?.data?.rewritten_email_body?.formal_email;
      if (rewritten) {
        return new Promise((resolve) => {
          setRewrittenEmail(rewritten);

          // Set modal handlers
          setModalHandlers({
            onConfirm: () => {
              confirmRewrite(
                rewritten,
                senderEmail,
                receiverEmail,
                attachmentFile
              ); // Call confirmRewrite to send and update the history
              resolve(rewritten);
            },
            onCancel: () => {
              setShowModal(false);
              resolve(null);
            },
          });

          setShowModal(true); // Open the modal
        });
      }
    } catch (err) {
      console.error("Error rewriting email:", err);
    } finally {
      setLoadingRewrite(false);
    }

    return null;
  };

  const confirmRewrite = (
    rewritten,
    senderEmail,
    receiverEmail,
    attachmentFile
  ) => {
    if (rewritten && senderEmail && receiverEmail) {
      handleSendReply(rewritten, senderEmail, receiverEmail, attachmentFile);
    }
    setShowModal(false);
    setRewrittenEmail(null);
    setPendingReplyData(null);
  };

  useEffect(() => {
    const fetchApplicationData = async () => {
      const headers = {
        "X-Access-Token":
          "bjZDbAhu9GabUZZqqk64FbaXK9GcTErIeDu5sVc6Ryt2USIgbwQUmZdvUXSevxyF",
        "X-Refresh-Token":
          "f68LK2Y5Py5ErV0qz81j4wGo9iIH7S1Aw5lLos7wDUPyitYmTSQ1Lut8s9AVYYys",
        Accept: "application/json",
      };

      try {
        const response = await axios.get(
          `https://insureai-communicator-1074099157700.us-central1.run.app/api/v1/conversations/${selectedApplicationId}/history`,
          { headers }
        );
        setConversations(response.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching application data:", error);
        setError("No data available");
        setConversations([]);
      }
    };

    if (selectedApplicationId) {
      fetchApplicationData();
    }
  }, [selectedApplicationId]);

  useEffect(() => {
    if (conversations?.history?.messages) {
      const updatedProcessedMessages = conversations.history.messages.map(
        (message) => {
          const messageDate = new Date(message.date_time);
          return {
            ...message,
            isSender: message.sender_email === senderEmail,
            formattedDate: messageDate.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            formattedTime: messageDate.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            }),
            name:
              conversations.history.users.find(
                (u) => u.email_id === message.sender_email
              )?.description || "Unknown User",
          };
        }
      );
      setProcessedMessages(updatedProcessedMessages);
    } else {
      setProcessedMessages([]);
    }
  }, [conversations, senderEmail]);

  return (
    <>
      <IconButton
        onClick={togglePopup}
        style={{
          position: "fixed",
          top: "80px",
          right: "20px",
          backgroundColor: "rgb(234, 88, 12)",
          color: "#fff",
          borderRadius: "50px",
          padding: "8px 12px",
          zIndex: 1000,
        }}
        className="email-conversation"
      >
        <MarkEmailReadOutlinedIcon />
      </IconButton>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        style={{ zIndex: 9999 }}
      >
        <Box
          sx={{
            position: "fixed",
            top: "60%",
            left: "82%",
            transform: "translate(-50%, -50%)",
            width: 400,
            boxShadow: 24,
            p: 4,
            backgroundColor: "#fff",
            borderRadius: "8px",
            zIndex: 9999,
          }}
        >
          <Typography variant="h6" mb={2}>
            Confirm and Edit Message
          </Typography>

          {loadingRewrite ? (
            <CircularProgress />
          ) : (
            <>
              <TextField
                label="From"
                fullWidth
                margin="dense"
                value={pendingReplyData?.senderEmail || ""}
                disabled
              />

              <TextField
                label="To"
                fullWidth
                margin="dense"
                value={pendingReplyData?.receiverEmail || ""}
                disabled
              />

              <TextField
                label="Email Body"
                multiline
                rows={6}
                fullWidth
                margin="dense"
                value={rewrittenEmail || ""}
                onChange={(e) => setRewrittenEmail(e.target.value)}
              />

              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button
                  style={{ color: "#f26522" }}
                  onClick={() => {
                    setShowModal(false);
                    modalHandlers?.onCancel?.(); // <-- invoke cancel callback if exists
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  style={{ backgroundColor: "#f26522" }}
                  onClick={() => {
                    setShowModal(false);
                    modalHandlers?.onConfirm?.(); // <-- invoke confirm callback if exists
                  }}
                >
                  Send
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {open && (
        <Box
          ref={popupRef}
          style={{
            position: "fixed",
            top: "125px",
            right: "1%",
            height: "75%",
            backgroundColor: "#f1f2f7",
            borderRadius: "12px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
            zIndex: 9998,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
          className="history-window"
        >
          {activeView === "chat" && (
            <Chat
              selectedApplicationName={selectedApplicationName}
              conversations={conversations}
              processedMessages={processedMessages}
              togglePopup={togglePopup}
              setActiveView={setActiveView}
              selectedApplicationId={selectedApplicationId}
              handleSendReply={handleRewriteAndConfirm}
              senderEmail={senderEmail}
              error={error}
              loadingRewrite={loadingRewrite}
            />
          )}

          {activeView === "details" && (
            <Media
              selectedApplicationName={selectedApplicationName}
              setActiveView={setActiveView}
              togglePopup={togglePopup}
              mediaFiles={mediaFiles}
              conversations={conversations}
            />
          )}
        </Box>
      )}
    </>
  );
};

export default Communicator;
